import type { InputHTMLAttributes, ReactNode } from "react";
import PhoneInput, { type Value } from "react-phone-number-input";
import ru from "react-phone-number-input/locale/ru.json";
import { Loader2 } from "lucide-react";
import "react-phone-number-input/style.css";

import { cn } from "@/lib/utils";

type WmtFormInputProps = InputHTMLAttributes<HTMLInputElement> & {
  error?: string;
};

export const WmtFormInput = ({
  error,
  className,
  ...props
}: WmtFormInputProps) => (
  <div>
    <input
      {...props}
      aria-invalid={Boolean(error)}
      className={cn(
        "w-full rounded-lg border bg-card px-4 py-3 font-body text-sm text-foreground transition-[border-color,box-shadow,background-color] placeholder:text-muted-foreground focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent",
        error ? "border-destructive" : "border-border",
        className,
      )}
    />
    {error && <p className="mt-1 text-xs text-destructive">{error}</p>}
  </div>
);

type WmtPhoneInputProps = {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  error?: string;
  helperText?: string;
  placeholder?: string;
  className?: string;
};

export const WmtPhoneInput = ({
  id,
  value,
  onChange,
  error,
  helperText,
  placeholder = "Телефон",
  className,
}: WmtPhoneInputProps) => (
  <div>
    <PhoneInput
      id={id}
      international
      defaultCountry="RU"
      countryCallingCodeEditable={false}
      labels={{ ...ru, BY: "Беларусь" }}
      placeholder={placeholder}
      value={(value || undefined) as Value | undefined}
      onChange={(nextValue) => onChange(nextValue || "")}
      aria-invalid={Boolean(error)}
      className={cn(
        "wmt-phone-input w-full rounded-lg border bg-card px-4 py-3 font-body text-sm text-foreground transition-[border-color,box-shadow,background-color] focus-within:border-accent focus-within:ring-1 focus-within:ring-accent",
        error ? "border-destructive" : "border-border",
        className,
      )}
    />
    {error ? (
      <p className="mt-1 text-xs text-destructive">{error}</p>
    ) : helperText ? (
      <p className="mt-1.5 text-left text-[11px] text-muted-foreground/70">
        {helperText}
      </p>
    ) : null}
  </div>
);

type WmtFormSubmitButtonProps = {
  submitting: boolean;
  children: ReactNode;
  submittingLabel?: string;
  className?: string;
};

export const WmtFormSubmitButton = ({
  submitting,
  children,
  submittingLabel = "Отправка…",
  className,
}: WmtFormSubmitButtonProps) => (
  <button
    type="submit"
    disabled={submitting}
    className={cn(
      "wmt-cta wmt-cta--dark w-full sm:w-auto sm:px-12",
      className,
    )}
  >
    {submitting ? (
      <span className="flex items-center gap-2">
        <Loader2 className="animate-spin" size={18} />
        {submittingLabel}
      </span>
    ) : (
      children
    )}
  </button>
);
